import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Search, Users, Crown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const DirectoryPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    // Check user status
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setIsPremium(
        userData.subscription_tier === 'pro' || 
        userData.subscription_tier === 'premium' || 
        userData.subscription_tier === 'enterprise'
      );
    }
    
    loadDirectory();
  }, []);

  useEffect(() => {
    // Filter entries based on search term
    if (searchTerm) {
      const filtered = entries.filter(entry =>
        entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.credential_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEntries(filtered);
    } else {
      setFilteredEntries(entries);
    }
  }, [searchTerm, entries]);

  const loadDirectory = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getDirectoryEntries();
      setEntries(data.entries || []);
      setFilteredEntries(data.entries || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Error loading directory:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="md" data-testid="brand-logo-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl mb-6 shadow-lg">
            <BadgeCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-slate-800 dark:text-slate-100">
            Verified Professionals
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            Professionals listed here have completed KaiCheckIt identity and credential verification.
            Listing is optional and does not imply endorsement.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-600 dark:text-blue-400 font-semibold">{totalCount} active professionals</span>
          </div>
        </div>

        {/* Premium user notice - distraction-free experience */}
        {isPremium && (
          <Alert className="mb-8 max-w-2xl mx-auto bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800">
            <Crown className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <AlertDescription className="text-purple-800 dark:text-purple-200">
              As a Premium member, you have full access to the directory here while enjoying a distraction-free experience on search results.
            </AlertDescription>
          </Alert>
        )}

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name or credential type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Directory Entries */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
            <p className="text-slate-600 dark:text-slate-300">Loading directory...</p>
          </div>
        ) : filteredEntries.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredEntries.map((entry) => (
              <Card key={entry.entry_id} className="hover:shadow-lg transition-shadow duration-200 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-lg">{entry.name}</h3>
                      <p className="text-slate-600 dark:text-slate-400">{entry.credential_type}</p>
                    </div>
                    <Badge className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800">
                      <BadgeCheck className="w-3 h-3 mr-1" />
                      {entry.verification_status}
                    </Badge>
                  </div>
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-700">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Verification valid through <span className="font-medium text-slate-700 dark:text-slate-300">{entry.verification_expiry}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {searchTerm ? 'No matching professionals found' : 'No verified professionals yet'}
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Be among the first to get KaiCheckIt verified!'}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => navigate('/influencer')}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                Apply for Verification
              </Button>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-slate-700 dark:text-slate-200 mb-2">Disclaimer</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                The KaiCheckIt Directory lists professionals who have completed identity and credential verification.
                KaiCheckIt does not recommend, rank, or endorse any individual.
                Directory listings are informational only and do not constitute medical or professional advice.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA for non-verified users */}
        {!isPremium && (
          <div className="mt-12 max-w-2xl mx-auto">
            <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              <CardContent className="pt-6 text-center">
                <Crown className="w-10 h-10 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Go Distraction-Free</h3>
                <p className="text-purple-100 mb-4">
                  Premium removes directory listings from search results and keeps the focus on evidence.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-white text-purple-600 hover:bg-slate-100"
                >
                  Upgrade to Premium
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Privacy Policy</a>
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DirectoryPage;
